import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReadingResult } from "./ReadingResult";
import { api, errorMessage } from "@/lib/api";

export const DocumentReadingDialog = ({ workspace, doc, onClose }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const base = doc ? `/workspaces/${workspace.id}/documents/${doc.id}` : null;

  useEffect(() => {
    if (!base) return;
    setState(null); setError("");
    api.get(`${base}/ai-reading`).then(({ data }) => setState(data)).catch((e) => setError(errorMessage(e)));
  }, [base]);

  const read = async () => {
    setBusy(true); setError("");
    try { const { data } = await api.post(`${base}/ai-read`); setState(data); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const dateAdded = (i, id) => setState((s) => {
    const dates = s.reading.result.dates.map((d, n) => n === i ? { ...d, deadline_id: id } : d);
    return { ...s, reading: { ...s.reading, result: { ...s.reading.result, dates } } };
  });

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} data-testid="doc-reading-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="pr-6 font-display text-navy">AI reading: <span className="break-all">{doc?.original_filename}</span></DialogTitle></DialogHeader>
        <p data-testid="doc-reading-notice" className="rounded-md border border-gold/40 bg-gold-light/40 p-3 text-xs text-slate-700">
          AI explains the document in plain language and can be wrong. Check every detail against the document itself. Dates stay unconfirmed until you check them and save them as deadlines. To create the reading, the document's text is sent to our AI provider. Numbers that look like Social Security or tax ID numbers are removed first. This is not legal or tax advice.
        </p>
        {state?.reading && <ReadingResult workspace={workspace} reading={state.reading} onDateAdded={dateAdded} />}
        {state && !state.reading && <p className="text-sm text-slate-600" data-testid="doc-reading-empty">This document hasn't been read yet.</p>}
        {error && <p className="text-sm text-red-700" data-testid="doc-reading-error">{error}</p>}
        {state && (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={read} disabled={busy} data-testid="doc-reading-run-button" className="bg-navy hover:bg-navy-2">
              <Sparkles className="mr-1.5 h-4 w-4 text-gold" /> {busy ? "Reading the document..." : state.reading ? "Read again" : "Read with AI"}
            </Button>
            <span className="text-xs text-slate-500" data-testid="doc-reading-usage">{state.used_today} of {state.daily_limit} readings used today in this workspace</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
